import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseModal } from 'shared/abstract/base-modal';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { filter, Subscription, switchMap, tap, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AccessGroupsService } from 'users/services/access-groups.service';
import { AccessGroup } from 'users/interfaces/access-group.interface';
import { ListingAccessFilter } from 'users/interfaces/listing-access-filter';
import { Brand } from 'settings/interfaces/brand.interface';
import { Supplier } from 'settings/interfaces/supplier.interface';
import { SuppliersService } from 'settings/services/suppliers.service';
import { EventBusService, ToastService, UserApiService } from 'shared/services';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { mutuallyExclusiveRequiredValidator } from 'shared/validators/mutually-exclusive-required-validator';
import { ListingsGridService } from 'listings/services/listings-grid.service';
import clone from 'lodash-es/clone';

@Component({
    selector: 'app-create-access-group',
    templateUrl: './create-access-group.component.html',
    styleUrls: ['./create-access-group.component.scss'],
    standalone: false,
})
export class CreateAccessGroupComponent extends BaseModal implements OnInit, OnDestroy {
    public data!: any;

    public showFormError = false;
    public showListingsFilter = false;
    public suppliers: Supplier[] = [];
    public accessGroupFilters!: AccessGroup;
    public selectedSupplierId!: string;
    public selectedSupplierName!: string;
    public userIsAdmin!: boolean;

    public dynamicOptions: { getDataFunction: Function; getDataFunctionParams?: any } = {
        getDataFunction: this.listingsService.getFilteredListingsForAccessGroups.bind(this.listingsService),
    };

    public createAccessGroupForm = new UntypedFormGroup(
        {
            companyName: new UntypedFormControl(null, Validators.required),
            accessGroupName: new UntypedFormControl(null, [Validators.required, Validators.maxLength(64)]),
            brands: new UntypedFormControl(null),
            countries: new UntypedFormControl(null),
            listings: new UntypedFormControl(null),
        },
        mutuallyExclusiveRequiredValidator([{ controlName: 'brands' }, { controlName: 'countries' }, { controlName: 'listings' }]),
    );

    private subscriptions: { [key: string]: Subscription | undefined } = {};

    constructor(
        private eventBusService: EventBusService,
        private accessGroupService: AccessGroupsService,
        private suppliersGridService: SuppliersService,
        private userApiService: UserApiService,
        public listingsService: ListingsGridService,
        private translate: TranslateService,
        private toastService: ToastService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.closeModalEvent = this.eventBusService
            .getObservableForEventStream('closeUserModal')
            .subscribe(() => this.closeModalInstance(undefined));

        this.subscriptions.getLoginInformation = this.userApiService.getLoginInformation().subscribe((userInformation) => {
            this.userIsAdmin = userInformation.isShiji;

            if (userInformation.isShiji) {
                this.subscriptions.getSuppliers = this.suppliersGridService
                    .getAllSuppliers()
                    .subscribe((suppliers) => (this.suppliers = suppliers));

                this.subscriptions.supplierChange = this.createAccessGroupForm
                    .get('companyName')
                    ?.valueChanges?.pipe(filter(Boolean))
                    .subscribe((value) => {
                        this.selectedSupplierId = value;
                        this.selectedSupplierName = this.suppliers.find((supplier) => supplier.id === value)?.companyName ?? '';
                        this.dynamicOptions = clone(this.dynamicOptions);
                        this.dynamicOptions.getDataFunctionParams = [this.selectedSupplierId, this.selectedSupplierName];
                        this.getUpdatedFilters(value);
                    });
            } else {
                userInformation = userInformation as CurrentSupplierUserInterface;

                this.subscriptions.supplier = this.suppliersGridService
                    .getSupplierPublicData(userInformation.supplierId!)
                    .subscribe((supplier) => (this.suppliers = [supplier]));

                this.selectedSupplierId = userInformation.supplierId;
                this.selectedSupplierName = this.suppliers.find((supplier) => supplier.id === this.selectedSupplierId)?.companyName ?? '';
                this.dynamicOptions.getDataFunctionParams = [this.selectedSupplierId, this.selectedSupplierName];

                this.createAccessGroupForm.get('companyName')?.setValue(userInformation.supplierId);
                this.createAccessGroupForm.get('companyName')?.disable();
                this.getUpdatedFilters(userInformation.supplierId!);
            }
        });

        this.subscriptions.brandsChange = this.createAccessGroupForm.get('brands')?.valueChanges?.subscribe((value) => {
            if (this.selectedSupplierId) {
                this.getUpdatedFilters(this.selectedSupplierId, value, this.createAccessGroupForm.value.countries);
            }
        });

        this.subscriptions.countriesChange = this.createAccessGroupForm.get('countries')?.valueChanges?.subscribe((value) => {
            if (this.selectedSupplierId) {
                this.getUpdatedFilters(this.selectedSupplierId, this.createAccessGroupForm.value.brands, value);
            }
        });
    }

    private getUpdatedFilters(supplierId: string, brandIds?: string[] | null, countryIds?: string[] | null) {
        const params: ListingAccessFilter = {
            supplierId: supplierId,
        };

        if (brandIds) {
            params.brandIds = brandIds;
        }

        if (countryIds) {
            params.countryIds = countryIds;
        }

        let selectedBrands: any;
        let selectedCountries: any;

        this.subscriptions.listingAccessFilter = this.accessGroupService
            .getListingAccessFilter(params)
            .pipe(
                tap((filters) => (this.accessGroupFilters = filters)),
                switchMap((filters) => {
                    const brandsControl = this.createAccessGroupForm.get('brands') as UntypedFormControl;
                    const countriesControl = this.createAccessGroupForm.get('countries') as UntypedFormControl;

                    if (brandsControl.value) {
                        selectedBrands = brandsControl.value.filter((brandId: string) =>
                            filters.brands.find((brand: Brand) => brand.id === brandId),
                        );
                        brandsControl.setValue(selectedBrands, { emitEvent: false });
                    }

                    if (countriesControl.value) {
                        selectedCountries = countriesControl.value.filter((countryId: string) =>
                            filters.countries.find((country: any) => country.id.toString() === countryId.toString()),
                        );
                        countriesControl.setValue(selectedCountries, { emitEvent: false });
                    }

                    params.brandIds = brandsControl.value;
                    params.countryIds = countriesControl.value;

                    return this.accessGroupService.getListingAccessFilter(params);
                }),
            )
            .subscribe((filterResults) => {
                this.accessGroupFilters.countries = filterResults.countries;
                this.accessGroupFilters.brands = filterResults.brands;
            });
    }

    public createAccessGroup() {
        this.createAccessGroupForm.markAllAsTouched();
        this.createAccessGroupForm.get('accessGroupName')?.updateValueAndValidity();
        this.showFormError = true;

        if (this.createAccessGroupForm.valid) {
            const body = {
                supplierId: this.createAccessGroupForm.get('companyName')?.value,
                name: this.createAccessGroupForm.value.accessGroupName,
                listingAccessesInput: [].concat(
                    this.createAccessGroupForm.value.brands?.map((brandId: string) => {
                        return { accessIdValue: brandId, accessType: 'BRAND' };
                    }) ?? [],
                    this.createAccessGroupForm.value.countries?.map((countryId: string) => {
                        return { accessIdValue: countryId, accessType: 'COUNTRY' };
                    }) ?? [],
                    this.createAccessGroupForm.value.listings?.map((listingId: string) => {
                        return { accessIdValue: listingId, accessType: 'LISTING' };
                    }) ?? [],
                ),
            };

            this.subscriptions.createListingAccessGroup = this.accessGroupService.createListingAccessGroup(body).subscribe((response) => {
                if (response.statusCode.toString().toLowerCase() === 'conflict') {
                    this.toastService.showToast('users.accessGroups.createEdit.alreadyExists', 'error', {
                        groupName: this.createAccessGroupForm.value.accessGroupName,
                    });
                    throwError(() => new Error('A group with that name already exists.'));
                } else {
                    this.toastService.showToast('users.accessGroups.createEdit.createSuccess', 'success', {
                        groupName: this.createAccessGroupForm.value.accessGroupName,
                    });
                    this.closeModal(true);
                }
            });
        }
    }

    public closeModal(result: boolean): void {
        this.closeModalInstance(result);
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }
}
